/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  Input,
  OnInit,
  OnDestroy,
  signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject, forkJoin, takeUntil, of, Observable } from "rxjs";
import { map, switchMap } from "rxjs/operators";

import { ConfirmationService, MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { InputTextModule } from "primeng/inputtext";
import { SelectModule } from "primeng/select";
import { SkeletonModule } from "primeng/skeleton";
import { StepperModule } from "primeng/stepper";
import { ToastModule } from "primeng/toast";
import { TooltipModule } from "primeng/tooltip";

import {
  QPilotAccessToken,
  QPilotService,
  QPilotUser,
} from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";
import {
  Survey,
  SurveyAnswer,
  SurveyAnswerType,
  SurveyFormControls,
  SurveyAnswerFormControls,
  OfferApplicableItemFormControls,
  OfferApplicableItem,
  OfferType,
  PredefinedAnswer,
  ACTION_TRIGGER_OPTIONS,
  DEFAULT_PAUSE_ANSWERS,
  DEFAULT_CANCEL_ANSWERS,
} from "../../interfaces/retention-workflow.interface";
import { RetentionWorkflowService } from "../../services/retention-workflow.service";
import {
  ACTION_TRIGGERS,
  SURVEY_VALIDATION,
  SURVEY_TOAST_MESSAGES,
  DEFAULT_SURVEY_QUESTIONS,
  MAX_WORKFLOWS,
  MAX_WORKFLOWS_MESSAGE,
} from "../../constants/retention-workflow.constants";
import { SurveyAnswerFormComponent } from "../../components/survey-answer-form/survey-answer-form.component";
import { DynamicFieldsPopoverComponent } from "../../components/dynamic-fields-popover/dynamic-fields-popover.component";

type PageMode = "create" | "edit" | "view";

@Component({
  selector: "app-retention-workflow-create-edit",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    CardModule,
    ConfirmDialogModule,
    InputTextModule,
    SelectModule,
    SkeletonModule,
    StepperModule,
    ToastModule,
    TooltipModule,
    SurveyAnswerFormComponent,
    DynamicFieldsPopoverComponent,
  ],
  templateUrl: "./retention-workflow-create-edit.component.html",
  styleUrl: "./retention-workflow-create-edit.component.scss",
  providers: [ConfirmationService],
  // Note: MessageService is NOT provided locally - we use the root instance
  // This ensures both component toast calls AND API interceptor error toasts
  // go to the same MessageService that the <p-toast> listens to
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RetentionWorkflowCreateEditComponent implements OnInit, OnDestroy {
  // Input properties for embedded mode
  @Input() inputSiteId?: number;
  @Input() inputToken?: QPilotAccessToken;
  @Input() inputWorkflowId?: string;
  @Input() inputMode?: PageMode;
  @Input() embeddedMode: boolean = false;
  @Input() hideToolbar: boolean = false;

  private readonly destroy$ = new Subject<void>();

  // Page state
  mode: PageMode = "create";
  surveyId: string | null = null;
  siteId: number | null = null;
  loading = signal(true);
  saving = signal(false);
  hasCustomerAnswer = false;
  showViewModeBanner = signal(true); // For dismissible banner in VIEW mode

  // Stepper state
  currentStep = signal<number>(1);

  // Max workflows
  readonly maxWorkflows = MAX_WORKFLOWS;
  readonly maxWorkflowsMessage = MAX_WORKFLOWS_MESSAGE;
  maxWorkflowsReached = signal(false);

  // Form
  surveyForm!: FormGroup<SurveyFormControls>;

  // Options
  readonly actionTriggerOptions = ACTION_TRIGGER_OPTIONS;
  readonly validation = SURVEY_VALIDATION;

  // Predefined answers for autocomplete
  predefinedAnswers: PredefinedAnswer[] = [];

  // Pre-loaded products and coupons (keyed by ID/Code)
  preloadedProducts: Map<string, { Id: number; Title: string; Sku?: string }> =
    new Map();
  preloadedCoupons: Map<string, { Id: number; Code: string; Amount?: number }> =
    new Map();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly sessionService: SessionService,
    private readonly retentionService: RetentionWorkflowService,
    private readonly qpilotService: QPilotService,
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly cdr: ChangeDetectorRef,
    private readonly elementRef: ElementRef,
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    // Handle embedded mode inputs (web component / iframe)
    const element = this.elementRef.nativeElement as HTMLElement;

    // Check HTML attribute for embedded mode (web component mode)
    const embeddedModeAttr =
      element.getAttribute("embedded-mode") ??
      element.getAttribute("embeddedMode");

    if (embeddedModeAttr === "true" || embeddedModeAttr === "") {
      this.embeddedMode = true;
    }

    // Get siteId from input/attribute
    const siteIdString =
      this.inputSiteId?.toString() ??
      element.getAttribute("input-site-id") ??
      element.getAttribute("inputSiteId");

    if (siteIdString) {
      this.siteId = parseInt(siteIdString, 10);
    }

    // Get workflowId from input/attribute
    const workflowIdString =
      this.inputWorkflowId ??
      element.getAttribute("input-workflow-id") ??
      element.getAttribute("inputWorkflowId");

    if (workflowIdString) {
      this.surveyId = workflowIdString;
    }

    // Get mode from input/attribute
    const modeString =
      this.inputMode ??
      element.getAttribute("input-mode") ??
      element.getAttribute("inputMode");

    if (modeString) {
      this.mode = modeString as PageMode;
    }

    // Get token from attribute and convert
    const tokenString =
      element.getAttribute("input-token") ?? element.getAttribute("inputToken");

    // Create inputToken from attribute if:
    // 1. We have a token string from attribute
    // 2. AND inputToken is either missing, not an object, or doesn't have valid TokenBearerAuth
    // Note: Angular Elements may bind the attribute as a raw string, so we check for proper structure
    if (
      tokenString &&
      (!this.inputToken ||
        typeof this.inputToken !== "object" ||
        !this.inputToken.TokenBearerAuth)
    ) {
      this.inputToken = {
        TokenBearerAuth: tokenString,
        TokenAuth: "",
        Id: 0,
        UserId: 0,
        CreatedUtc: new Date(),
        UpdatedUtc: new Date(),
        ExpiresUtc: new Date(),
        User: {} as QPilotUser,
        FirstLogin: 0,
      };
    }

    // Initialize based on mode
    if (this.embeddedMode && this.inputToken && this.siteId) {
      this.loadAllData();
    } else if (!this.embeddedMode) {
      this.determineMode();
      this.loadSiteId();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.surveyForm = new FormGroup<SurveyFormControls>({
      actionTrigger: new FormControl<number | null>(null, {
        validators: [Validators.required],
      }),
      name: new FormControl<string>("", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.minLength(SURVEY_VALIDATION.name.minLength),
          Validators.maxLength(SURVEY_VALIDATION.name.maxLength),
        ],
      }),
      question: new FormControl<string>("", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.minLength(SURVEY_VALIDATION.question.minLength),
          Validators.maxLength(SURVEY_VALIDATION.question.maxLength),
        ],
      }),
      answers: new FormArray<FormGroup<SurveyAnswerFormControls>>([]),
    });

    // Listen to action trigger changes to set defaults
    this.surveyForm.controls.actionTrigger.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((trigger) => {
        if (trigger !== null && this.mode === "create") {
          this.onTriggerSelected(trigger);
        }
      });
  }

  private determineMode(): void {
    const url = this.router.url;
    if (url.includes("/create")) {
      this.mode = "create";
      // Loading will be set to false after loadAllData completes
    } else if (url.includes("/view")) {
      this.mode = "view";
      this.surveyId = this.route.snapshot.paramMap.get("id");
    } else if (url.includes("/edit")) {
      this.mode = "edit";
      this.surveyId = this.route.snapshot.paramMap.get("id");
    }
  }

  private loadSiteId(): void {
    // Get siteId from parent route (widgets/dashboard/:siteId)
    this.route.parent?.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const siteIdParam = params.get("siteId");
        if (siteIdParam) {
          this.siteId = parseInt(siteIdParam, 10);
          this.loadAllData();
        }
      });
  }

  /**
   * Get access token based on mode (embedded vs standard)
   */
  private getAccessToken(): QPilotAccessToken | undefined {
    if (this.embeddedMode && this.inputToken) {
      return this.inputToken;
    }
    return this.sessionService.accessToken ?? undefined;
  }

  private loadAllData(): void {
    const accessToken = this.getAccessToken();
    if (!this.siteId || !accessToken) return;

    this.loading.set(true);

    // Load predefined answers (used in all modes)
    const predefinedAnswers$ = this.retentionService
      .getPredefinedAnswers(this.siteId, accessToken)
      .pipe(takeUntil(this.destroy$));

    // Load survey if editing/viewing, otherwise just return null
    const survey$ = this.surveyId
      ? this.retentionService.getSurveyById(
          this.siteId,
          this.surveyId,
          accessToken,
        )
      : of(null);

    // Check workflow count in create mode
    const workflowCount$ =
      this.mode === "create"
        ? this.retentionService
            .getSurveys(this.siteId, accessToken)
            .pipe(takeUntil(this.destroy$))
        : of(null);

    // First load predefined answers, survey data, and workflow count
    forkJoin({
      predefinedAnswers: predefinedAnswers$,
      surveyResponse: survey$,
      workflowCountResponse: workflowCount$,
    })
      .pipe(
        takeUntil(this.destroy$),
        // Then load products/coupons based on survey answers
        switchMap(
          ({ predefinedAnswers, surveyResponse, workflowCountResponse }) => {
            this.predefinedAnswers = predefinedAnswers || [];

            // Check if max workflows reached (for create mode)
            if (workflowCountResponse?.result?.surveys) {
              const count = workflowCountResponse.result.surveys.length;
              this.maxWorkflowsReached.set(count >= MAX_WORKFLOWS);
            }

            if (!surveyResponse) {
              // No survey to load, we're done - return consistent shape
              return of({
                surveyResponse: null,
                products: [] as Array<{
                  Id: number;
                  Title: string;
                  Sku?: string;
                } | null>,
                coupons: [] as Array<{
                  Id: number;
                  Code: string;
                  Amount?: number;
                } | null>,
              });
            }

            // Extract product and coupon IDs from survey answers
            const productIds: string[] = [];
            const couponCodes: string[] = [];

            surveyResponse.survey.answers.forEach((answer: SurveyAnswer) => {
              const offer = answer.offerApplicableItem;
              if (offer?.itemId) {
                if (offer.itemType === OfferType.FreeProduct) {
                  productIds.push(offer.itemId);
                } else if (offer.itemType === OfferType.Discount) {
                  couponCodes.push(offer.itemId);
                }
              }
            });

            // Create observables for fetching products and coupons
            const productFetches$ = this.fetchProductsById(productIds);
            const couponFetches$ = this.fetchCouponsByCodes(couponCodes);

            return forkJoin({
              products: productFetches$,
              coupons: couponFetches$,
            }).pipe(
              map((results) => ({
                surveyResponse,
                products: results.products,
                coupons: results.coupons,
              })),
            );
          },
        ),
      )
      .subscribe({
        next: (result) => {
          // Store pre-loaded products and coupons
          if (result.products) {
            result.products.forEach(
              (product: { Id: number; Title: string; Sku?: string } | null) => {
                if (product) {
                  this.preloadedProducts.set(product.Id.toString(), product);
                }
              },
            );
          }

          if (result.coupons) {
            result.coupons.forEach(
              (
                coupon: { Id: number; Code: string; Amount?: number } | null,
              ) => {
                if (coupon) {
                  this.preloadedCoupons.set(coupon.Code, coupon);
                }
              },
            );
          }

          // Populate form if survey was loaded
          if (result.surveyResponse) {
            this.hasCustomerAnswer = result.surveyResponse.hasCustomerAnswer;
            this.populateForm(result.surveyResponse.survey);
          }

          this.loading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: SURVEY_TOAST_MESSAGES.loadSingleError,
          });
          this.loading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Fetch multiple products by their IDs
   */
  private fetchProductsById(
    productIds: string[],
  ): Observable<Array<{ Id: number; Title: string; Sku?: string } | null>> {
    const accessToken = this.getAccessToken();
    if (productIds.length === 0 || !this.siteId || !accessToken) {
      return of([]);
    }

    const fetches = productIds.map((productId) =>
      this.qpilotService
        .getProductsForSite(this.siteId!, accessToken, 1, productId)
        .pipe(
          map((products) => {
            const product = products?.find(
              (p: { Id: number }) => p.Id.toString() === productId,
            );
            return product || null;
          }),
        ),
    );

    return forkJoin(fetches);
  }

  /**
   * Fetch multiple coupons by their codes
   */
  private fetchCouponsByCodes(
    couponCodes: string[],
  ): Observable<Array<{ Id: number; Code: string; Amount?: number } | null>> {
    const accessToken = this.getAccessToken();
    if (couponCodes.length === 0 || !this.siteId || !accessToken) {
      return of([]);
    }

    const fetches = couponCodes.map((couponCode) =>
      this.qpilotService
        .getCouponsForSite(this.siteId!, accessToken, 1, couponCode)
        .pipe(
          map((response) => {
            const coupon = response?.Items?.find(
              (c: { Code: string }) => c.Code === couponCode,
            );
            return coupon || null;
          }),
        ),
    );

    return forkJoin(fetches);
  }

  private populateForm(survey: Survey): void {
    // Set basic fields
    this.surveyForm.patchValue({
      actionTrigger: parseInt(survey.actionTrigger, 10),
      name: survey.name,
      question: survey.question,
    });

    // Clear and populate answers
    this.answersArray.clear();
    survey.answers.forEach((answer) => {
      this.addAnswerFromData(answer);
    });

    // Disable form in view mode
    if (this.mode === "view") {
      this.surveyForm.disable();
    }

    // Set stepper: VIEW mode starts at step 1, EDIT mode starts at step 2
    if (this.mode === "view") {
      this.currentStep.set(1);
    } else {
      this.currentStep.set(2);
    }
  }

  private onTriggerSelected(trigger: number): void {
    // Set default question based on trigger
    const currentQuestion = this.surveyForm.controls.question.value;
    if (!currentQuestion) {
      const defaultQuestion =
        trigger === ACTION_TRIGGERS.PAUSE
          ? DEFAULT_SURVEY_QUESTIONS.pause
          : DEFAULT_SURVEY_QUESTIONS.cancel;
      this.surveyForm.controls.question.setValue(defaultQuestion);
    }

    // Add default answers if no answers exist
    if (this.answersArray.length === 0) {
      const defaultAnswers =
        trigger === ACTION_TRIGGERS.PAUSE
          ? DEFAULT_PAUSE_ANSWERS
          : DEFAULT_CANCEL_ANSWERS;

      defaultAnswers.forEach((answer) => {
        this.addAnswerFromData(answer as SurveyAnswer);
      });
    }

    // Auto-advance to next step after selecting trigger
    this.currentStep.set(2);
  }

  get answersArray(): FormArray<FormGroup<SurveyAnswerFormControls>> {
    return this.surveyForm.controls.answers;
  }

  get triggerSelected(): boolean {
    return this.surveyForm.controls.actionTrigger.value !== null;
  }

  get isViewMode(): boolean {
    return (
      this.mode === "view" || (this.mode === "edit" && this.hasCustomerAnswer)
    );
  }

  get isEditMode(): boolean {
    return this.mode === "edit";
  }

  get pageTitle(): string {
    switch (this.mode) {
      case "create":
        return "Create Retention Workflow";
      case "edit":
        return this.hasCustomerAnswer
          ? "View Retention Workflow (Read-only)"
          : "Edit Retention Workflow";
      case "view":
        return "View Retention Workflow";
      default:
        return "Retention Workflow";
    }
  }

  get submitButtonLabel(): string {
    if (this.saving()) {
      return this.mode === "create" ? "Creating..." : "Saving...";
    }
    return this.mode === "create" ? "Create Workflow" : "Save Changes";
  }

  // Get selected trigger name for display
  getSelectedTriggerName(): string {
    const triggerValue = this.surveyForm.controls.actionTrigger.value;
    if (triggerValue === null) return "";
    const option = this.actionTriggerOptions.find(
      (opt) => opt.value === triggerValue,
    );
    return option?.displayName || "";
  }

  // Stepper navigation
  goToStep(step: number): void {
    this.currentStep.set(step);
  }

  isStepComplete(step: number): boolean {
    // In VIEW mode, all steps are considered complete (data is already loaded)
    if (this.mode === "view") {
      return true;
    }

    switch (step) {
      case 1:
        return this.surveyForm.controls.actionTrigger.value !== null;
      case 2:
        return (
          this.surveyForm.controls.name.valid &&
          this.surveyForm.controls.question.valid
        );
      case 3:
        return this.answersArray.length >= SURVEY_VALIDATION.minAnswers;
      default:
        return false;
    }
  }

  // Banner visibility methods for VIEW mode
  dismissBanner(): void {
    this.showViewModeBanner.set(false);
  }

  showBanner(): void {
    this.showViewModeBanner.set(true);
  }

  canProceedToStep(step: number): boolean {
    // Can always go back
    if (step < this.currentStep()) return true;
    // Can't skip steps - must complete previous steps
    for (let i = 1; i < step; i++) {
      if (!this.isStepComplete(i)) return false;
    }
    return true;
  }

  canDeleteAnswer(): boolean {
    return this.answersArray.length > SURVEY_VALIDATION.minAnswers;
  }

  canAddAnswer(): boolean {
    return this.answersArray.length < SURVEY_VALIDATION.maxAnswers;
  }

  // Answer management
  addAnswer(): void {
    if (!this.canAddAnswer()) return;

    const answerGroup = this.createAnswerFormGroup();
    this.answersArray.push(answerGroup);
  }

  removeAnswer(index: number): void {
    if (!this.canDeleteAnswer()) return;
    this.answersArray.removeAt(index);
  }

  private addAnswerFromData(answer: SurveyAnswer): void {
    const answerGroup = this.createAnswerFormGroup(answer);
    this.answersArray.push(answerGroup);
  }

  private createAnswerFormGroup(
    data?: SurveyAnswer,
  ): FormGroup<SurveyAnswerFormControls> {
    const isOptionalComment = data?.type === SurveyAnswerType.OptionalComment;
    const isRequiredComment = data?.type === SurveyAnswerType.RequiredComment;

    return new FormGroup<SurveyAnswerFormControls>({
      id: new FormControl<string | null>(data?.id || null),
      answer: new FormControl<string>(data?.answer || "", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.minLength(SURVEY_VALIDATION.answer.minLength),
          Validators.maxLength(SURVEY_VALIDATION.answer.maxLength),
        ],
      }),
      type: new FormControl<SurveyAnswerType>(
        data?.type ?? SurveyAnswerType.NoComment,
        { nonNullable: true },
      ),
      enableComment: new FormControl<boolean>(
        isOptionalComment || isRequiredComment,
        { nonNullable: true },
      ),
      requireComment: new FormControl<boolean>(isRequiredComment, {
        nonNullable: true,
      }),
      offerApplicableItem: new FormGroup<OfferApplicableItemFormControls>({
        id: new FormControl<string | null>(
          data?.offerApplicableItem?.id || null,
        ),
        itemId: new FormControl<string | null>(
          data?.offerApplicableItem?.itemId || null,
        ),
        itemType: new FormControl<OfferType | null>(
          data?.offerApplicableItem?.itemType ?? OfferType.NoOffer,
        ),
        maxCycles: new FormControl<number | null>(
          // Ensure number conversion in case API returns string
          data?.offerApplicableItem?.maxCycles != null
            ? Number(data.offerApplicableItem.maxCycles)
            : null,
        ),
        phrase: new FormControl<string | null>(
          data?.offerApplicableItem?.phrase || null,
        ),
        quantity: new FormControl<number | null>(
          // Ensure number conversion in case API returns string
          data?.offerApplicableItem?.quantity != null
            ? Number(data.offerApplicableItem.quantity)
            : null,
        ),
      }),
    });
  }

  // Navigation
  goBack(): void {
    if (this.embeddedMode) {
      // Emit event for parent to handle navigation
      const event = new CustomEvent("qmc-workflow-saved", {
        detail: {
          component: "retention-workflow-create-edit",
          action: "list",
          workflowId: this.surveyId,
          siteId: this.siteId,
        },
        bubbles: true,
        composed: true,
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      // Navigate back to the list
      if (this.siteId) {
        void this.router.navigate([
          "/widgets/dashboard",
          this.siteId,
          "retain-and-grow",
          "retention",
        ]);
      } else {
        void this.router.navigate(["/"]);
      }
    }
  }

  // Form submission
  onSubmit(): void {
    if (this.isViewMode) return;

    // Mark all fields as touched for validation
    this.surveyForm.markAllAsTouched();

    if (this.surveyForm.invalid) {
      this.messageService.add({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please fix the errors in the form before submitting.",
      });
      return;
    }

    this.saving.set(true);

    if (this.mode === "create") {
      this.createSurvey();
    } else {
      this.updateSurvey();
    }
  }

  private createSurvey(): void {
    const accessToken = this.getAccessToken();
    if (!this.siteId || !accessToken) return;

    // Pre-check if max workflows reached
    if (this.maxWorkflowsReached()) {
      this.messageService.add({
        severity: "error",
        summary: this.maxWorkflowsMessage.title,
        detail: SURVEY_TOAST_MESSAGES.maxWorkflowsReached,
      });
      this.saving.set(false);
      return;
    }

    const formValue = this.surveyForm.getRawValue();
    const answers = this.buildAnswersForRequest();

    const request = {
      siteId: this.siteId,
      name: formValue.name,
      question: formValue.question,
      actionTrigger: [formValue.actionTrigger!],
      answers,
      isEnabled: true,
    };

    this.retentionService
      .createSurvey(request, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: SURVEY_TOAST_MESSAGES.createSuccess,
          });
          this.saving.set(false);
          // Navigate back after short delay
          setTimeout(() => this.goBack(), 1000);
        },
        error: (error) => {
          // Check if error is about max workflows
          const errorMessage =
            typeof error?.error === "string" &&
            error.error.includes("10 surveys")
              ? SURVEY_TOAST_MESSAGES.maxWorkflowsReached
              : SURVEY_TOAST_MESSAGES.createError;
          const errorTitle =
            errorMessage === SURVEY_TOAST_MESSAGES.maxWorkflowsReached
              ? this.maxWorkflowsMessage.title
              : "Error";

          this.messageService.add({
            severity: "error",
            summary: errorTitle,
            detail: errorMessage,
          });

          // Update max workflows state if that was the error
          if (errorMessage === SURVEY_TOAST_MESSAGES.maxWorkflowsReached) {
            this.maxWorkflowsReached.set(true);
          }

          this.saving.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  private updateSurvey(): void {
    const accessToken = this.getAccessToken();
    if (!this.siteId || !this.surveyId || !accessToken) return;

    const formValue = this.surveyForm.getRawValue();
    const answers = this.buildAnswersForUpdate();

    const request = {
      id: parseInt(this.surveyId, 10),
      siteId: this.siteId,
      name: formValue.name,
      question: formValue.question,
      answers,
    };

    this.retentionService
      .updateSurvey(request, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: SURVEY_TOAST_MESSAGES.updateSuccess,
          });
          this.saving.set(false);
          // Navigate back after short delay
          setTimeout(() => this.goBack(), 1000);
        },
        error: () => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: SURVEY_TOAST_MESSAGES.updateError,
          });
          this.saving.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  private buildAnswersForRequest(): SurveyAnswer[] {
    return this.answersArray.controls.map((control) => {
      const value = control.getRawValue();
      const type = this.getAnswerType(
        value.enableComment,
        value.requireComment,
      );

      const itemType = value.offerApplicableItem.itemType ?? OfferType.NoOffer;

      // Build offerApplicableItem based on offer type
      // API requires this object to always be present
      let offerApplicableItem: OfferApplicableItem;

      if (itemType === OfferType.FreeProduct) {
        // Free Product requires: itemId (product), maxCycles, quantity
        offerApplicableItem = {
          itemType,
          itemId: value.offerApplicableItem.itemId,
          phrase: value.offerApplicableItem.phrase,
          maxCycles: value.offerApplicableItem.maxCycles,
          quantity: value.offerApplicableItem.quantity,
        };
      } else if (itemType === OfferType.Discount) {
        // Discount requires: itemId (coupon code)
        offerApplicableItem = {
          itemType,
          itemId: value.offerApplicableItem.itemId,
          phrase: value.offerApplicableItem.phrase,
        };
      } else {
        // NoOffer, ChangeFrequency, SkipCycle: itemId must be empty string
        offerApplicableItem = {
          itemType,
          itemId: "",
        };
      }

      const answer: SurveyAnswer = {
        answer: value.answer,
        type,
        offerApplicableItem,
      };

      return answer;
    });
  }

  private buildAnswersForUpdate(): {
    id: string;
    answer: string;
    offerItemPhrase?: string;
  }[] {
    return this.answersArray.controls.map((control) => {
      const value = control.getRawValue();
      return {
        id: value.id || "",
        answer: value.answer,
        offerItemPhrase: value.offerApplicableItem.phrase || undefined,
      };
    });
  }

  private getAnswerType(
    enableComment: boolean,
    requireComment: boolean,
  ): SurveyAnswerType {
    if (requireComment) {
      return SurveyAnswerType.RequiredComment;
    }
    if (enableComment) {
      return SurveyAnswerType.OptionalComment;
    }
    return SurveyAnswerType.NoComment;
  }
}
